import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLocale, useSetLocale } from '@refinedev/core';
import { Avatar, Button, Dropdown, Space, MenuProps } from 'antd';
import cn from 'classnames';
import styles from './languageSwitcher.module.scss';
import Global from '../ui/icons/global';
import Triangle from '../ui/icons/triangle';

type Lang = 'en' | 'uk';

const LABEL: Record<Lang, string> = {
    en: 'English',
    uk: 'Українська',
};

const SHORT: Record<Lang, string> = {
    en: 'Eng',
    uk: 'Укр',
};

const LanguageSwitcher: FC = () => {
    const { i18n } = useTranslation();
    const getLocale = useGetLocale();
    const setLocale = useSetLocale();
    const currentLocale = (getLocale() || 'uk') as Lang;

    const [open, setOpen] = useState(false);

    const items = useMemo<MenuProps['items']>(() => {
        const langs = (i18n.languages || ['uk', 'en'])
            .filter((l): l is Lang => l === 'uk' || l === 'en')
            .sort();

        return langs.map(lang => ({
            key: lang,
            label: LABEL[lang],
            icon: (
                <Avatar
                    size={16}
                    src={`/images/flags/${lang}.svg`}
                    alt={LABEL[lang]}
                />
            ),
        }));
    }, [i18n.languages]);

    const onClick: MenuProps['onClick'] = ({ key }) => {
        const lang = key as Lang;
        setLocale(lang);
        localStorage.setItem('lang', lang);
        setOpen(false);
    };

    return (
        <div className={styles.languageSwitcher}>
            <Dropdown
                open={open}
                onOpenChange={setOpen}
                placement="bottomLeft"
                trigger={['click']}
                menu={{
                    items,
                    onClick,
                    selectedKeys: [currentLocale],
                    className: styles.menu, // стилі на <ul>
                }}
                overlayClassName={styles.dropdownOverlay} // стилі на контейнер попапа
                getPopupContainer={trigger =>
                    trigger?.parentElement || document.body
                }
            >
                <Button
                    type="text"
                    className={styles.trigger}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Change language"
                >
                    <Space>
                        <Global className={styles.icon} />
                        <span className={styles.title}>
                            {SHORT[currentLocale]}
                        </span>
                        <span
                            className={cn(styles.indicator, {
                                [styles.activeIndicator]: open,
                            })}
                        >
                            <Triangle />
                        </span>
                    </Space>
                </Button>
            </Dropdown>
        </div>
    );
};

export default LanguageSwitcher;
